import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resellers } from '@/api/resellers';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InviteMethodToggle, type InviteMethod } from '@/components/InviteMethodToggle';
import { PhoneInput } from '@/components/PhoneInput';
import { TimezonePicker } from '@/components/TimezonePicker';
import { slugify } from '@/lib/slugify';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  // Live niceifier on the slug input means the value here is always
  // already valid; we still gate empty / too-short here to surface a
  // friendly error if the name produces no slug-able characters.
  slug: z.string().regex(/^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/, 'Enter a name with at least one letter or digit'),
  timezone: z.string().optional(),
  defaultLanguage: z.enum(['en', 'he', 'yi']).optional(),
  ownerEmail: z.string().email('Valid email required'),
  ownerDisplayName: z.string().optional(),
  ownerPhone: z.string().optional(),
});
type V = z.infer<typeof schema>;

const SELECT_CLS = 'flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export function ResellerNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [method, setMethod] = useState<InviteMethod>('manual_password');
  const [tempPassword, setTempPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { register, control, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<V>({
    resolver: zodResolver(schema),
    defaultValues: { defaultLanguage: 'en', name: '', slug: '' },
  });

  // ── Live slug niceifier ───────────────────────────────────────────
  // Auto-fill the slug from the name as the user types, until they
  // explicitly edit the slug themselves. Once touched, we stop syncing
  // so manual overrides survive further name edits. Slug input is
  // also normalized through slugify() on every keystroke so users can
  // never produce an invalid value.
  const watchedName = watch('name') ?? '';
  const watchedSlug = watch('slug') ?? '';
  const userTouchedSlug = useRef(false);
  const lastAutoSlug = useRef('');

  useEffect(() => {
    if (userTouchedSlug.current) return;
    const next = slugify(watchedName);
    if (next !== watchedSlug) {
      setValue('slug', next, { shouldValidate: false, shouldDirty: true });
    }
    lastAutoSlug.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedName]);

  function handleSlugChange(raw: string) {
    const normalized = slugify(raw);
    // If the user clears the slug back to empty, restore auto-sync so
    // typing in the name field starts filling it again.
    if (normalized === '') {
      userTouchedSlug.current = false;
    } else if (normalized !== lastAutoSlug.current) {
      // They typed something different from the auto-suggested slug.
      userTouchedSlug.current = true;
    }
    setValue('slug', normalized, { shouldValidate: false, shouldDirty: true });
  }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.resellers')}
        title={t('reseller.newTitle')}
        description={t('reseller.newDescription')}
        actions={
          <Button variant="outline" asChild>
            <Link to="/resellers">{t('common.back')}</Link>
          </Button>
        }
      />

      <form
        className="max-w-2xl space-y-6"
        noValidate
        onSubmit={handleSubmit(async (v) => {
          setError(null);
          try {
            await resellers.create({
              name: v.name, slug: v.slug,
              timezone: v.timezone, defaultLanguage: v.defaultLanguage,
              firstOwner: {
                email: v.ownerEmail,
                displayName: v.ownerDisplayName ?? null,
                phone: v.ownerPhone ?? null,
                method, manualPassword: method === 'manual_password' ? tempPassword : undefined,
              },
            });
            navigate('/resellers');
          } catch (err) {
            const code = (err as { code?: string }).code;
            if (code === 'email_already_exists' || code === 'email_taken' || code === 'email_already_in_use') {
              setError('This email is already in use.');
            } else {
              setError(err instanceof Error ? err.message : t('common.failedToSave'));
            }
          }
        })}
      >
        <Card>
          <CardHeader><CardTitle className="text-base">{t('reseller.tenantCard')}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="name">{t('common.name')}</Label>
                  <Input id="name" aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} {...register('name')} />
                  {errors.name && <p id="name-error" role="alert" className="text-xs text-destructive">{errors.name.message}</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="slug">{t('reseller.slug')}</Label>
                  <Input
                    id="slug"
                    placeholder="big-co"
                    aria-invalid={!!errors.slug}
                    aria-describedby={errors.slug ? 'slug-error' : 'slug-hint'}
                    value={watchedSlug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    className="font-mono"
                  />
                  <p id="slug-hint" className="text-[11px] text-muted-foreground">
                    Auto-filled from the name. Lowercase letters, digits, and hyphens only.
                  </p>
                  {errors.slug && <p id="slug-error" role="alert" className="text-xs text-destructive">{errors.slug.message}</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="timezone">{t('common.timezone')}</Label>
                  <Controller
                    name="timezone"
                    control={control}
                    render={({ field }) => (
                      <TimezonePicker value={field.value ?? ''} onChange={field.onChange} />
                    )}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="defaultLanguage">{t('reseller.defaultLanguage')}</Label>
                  <select id="defaultLanguage" className={SELECT_CLS} {...register('defaultLanguage')}>
                    <option value="en">English</option>
                    <option value="he">Hebrew</option>
                    <option value="yi">Yiddish</option>
                  </select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">{t('reseller.firstOwnerCard')}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="ownerEmail">{t('common.email')}</Label>
                <Input id="ownerEmail" type="email" aria-invalid={!!errors.ownerEmail} aria-describedby={errors.ownerEmail ? 'ownerEmail-error' : undefined} {...register('ownerEmail')} />
                {errors.ownerEmail && <p id="ownerEmail-error" role="alert" className="text-xs text-destructive">{errors.ownerEmail.message}</p>}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="ownerDisplayName">{t('users.displayName')}</Label>
                  <Input id="ownerDisplayName" {...register('ownerDisplayName')} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ownerPhone">{t('common.phone')}</Label>
                  {/* No tenant scope props — the reseller doesn't exist yet,
                      so the prefix resolves from the super-admin's own scope
                      (platform default → '+1'). */}
                  <Controller
                    name="ownerPhone"
                    control={control}
                    render={({ field }) => (
                      <PhoneInput
                        id="ownerPhone"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        placeholder="555 000 0000"
                      />
                    )}
                  />
                </div>
              </div>
              <InviteMethodToggle value={method} onChange={setMethod} tempPassword={tempPassword} onTempPasswordChange={setTempPassword} />
              {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('common.creating') : t('reseller.createReseller')}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/resellers')}>{t('common.cancel')}</Button>
        </div>
      </form>
    </>
  );
}
